import React, { useState } from "react";
import { useParams } from "react-router-dom";

import { WorkbookType } from "../Workbook";
import { useCreateWorkbookParameterSetsMutation } from "../ChooseParameters/gql/Mutation.create.generated";

import {
  paramSetFromExisting,
  toParameterSetPayload,
  validateParamSet,
  workbookDetailsRefetch,
} from "../Workbook/util";
import {
  ParameterSetInputGrid,
  useParamSetInputs,
} from "../ParameterSetInputs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle } from "@fortawesome/pro-solid-svg-icons";
import SQLEditor from "features/Workbooks/SQLEditor";

const CreateParameterSetPanel = ({
  workbook,
  addParamMethod,
  onParameterSetsCreated,
  initialCopyFromId,
}: {
  workbook: WorkbookType;
  addParamMethod: "query" | "manual";
  onParameterSetsCreated?: () => void;
  initialCopyFromId?: string;
}) => {
  const baselineQuery = workbook.baselineQuery;
  const { databaseId } = useParams();
  const [errorMessage, setErrorMessage] = useState("");
  const [customParamsQuery, setCustomParamsQuery] = useState("");
  const [copyFromId, setCopyFromId] = useState(initialCopyFromId ?? "");

  const {
    paramSet,
    setNewParamSet,
    handleParamValueChange,
    handleParamNullChange,
  } = useParamSetInputs(workbook, () =>
    paramSetFromExisting(
      workbook,
      workbook.aliasParamMapList.find((ap) => ap.id === initialCopyFromId)
        ?.parameters,
    ),
  );

  const [createWorkbookParameterSets] =
    useCreateWorkbookParameterSetsMutation();

  const refetchWorkbookDetails = workbookDetailsRefetch(
    workbook.id,
    databaseId,
  );

  const handleAddParamsFromQuery = () => {
    if (customParamsQuery == "") {
      setErrorMessage("Query is required");
      return;
    }
    createWorkbookParameterSets({
      variables: {
        databaseId,
        variantId: baselineQuery.id,
        queryText: customParamsQuery,
      },
      ...refetchWorkbookDetails,
      onCompleted: () => {
        setErrorMessage("");
        setCustomParamsQuery("");
      },
      onError: (error) => {
        setErrorMessage(error.message);
      },
    });
  };

  const handleAddParamsManual = () => {
    const validateError = validateParamSet(
      paramSet,
      workbook.parameterRefAliases.length,
    );
    if (validateError) {
      setErrorMessage(validateError);
      return;
    }
    createWorkbookParameterSets({
      variables: {
        databaseId,
        variantId: baselineQuery.id,
        parameterSet: toParameterSetPayload(paramSet),
      },
      ...refetchWorkbookDetails,
      onCompleted: () => {
        setErrorMessage("");
        setNewParamSet({});
        setCopyFromId("");
        onParameterSetsCreated?.();
      },
      onError: (error) => {
        setErrorMessage(error.message);
      },
    });
  };

  const handleCopyFromChange = (id: string) => {
    setCopyFromId(id);
    const source = workbook.aliasParamMapList.find((ap) => ap.id === id);
    setNewParamSet(paramSetFromExisting(workbook, source?.parameters));
  };

  return (
    <div className="grid gap-4">
      {addParamMethod === "query" ? (
        <div className="relative h-40">
          <SQLEditor
            className="absolute top-0 left-0 right-0 bottom-0 overflow-scroll"
            placeholder="Paste query with parameter values..."
            sql={customParamsQuery}
            onChange={setCustomParamsQuery}
          />
        </div>
      ) : (
        <div>
          {workbook.aliasParamMapList.length > 0 && (
            <div className="mb-4 flex items-center gap-2">
              <label
                htmlFor="copy-from-parameter-set"
                className="font-medium mb-0"
              >
                Copy values from
              </label>
              <select
                id="copy-from-parameter-set"
                className="bg-white rounded border border-gray-300 h-8 px-2"
                value={copyFromId}
                onChange={(e) => handleCopyFromChange(e.target.value)}
              >
                <option value="">Start from blank</option>
                {workbook.aliasParamMapList.map((ap) => (
                  <option key={ap.id} value={ap.id}>
                    {ap.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <ParameterSetInputGrid
            id="manual-parameter-inputs"
            workbook={workbook}
            paramSet={paramSet}
            onValueChange={handleParamValueChange}
            onNullChange={handleParamNullChange}
            className="mb-4"
          />
        </div>
      )}
      {errorMessage && (
        <div className="text-[#C22426]">
          <FontAwesomeIcon icon={faExclamationCircle} /> {errorMessage}
        </div>
      )}
      <div>
        <button
          className="btn btn-primary"
          onClick={
            addParamMethod === "query"
              ? handleAddParamsFromQuery
              : handleAddParamsManual
          }
        >
          Create Custom Parameter Set
        </button>
      </div>
    </div>
  );
};

export default CreateParameterSetPanel;
